import { apiClient } from '@/auth';

/**
 * Full recipe + recipe-list API surface for V2. First-class managed recipes
 * use the patient-visible library endpoints, while the legacy Edamam workflow
 * continues to use recipe lists:
 *
 *   GET  /patients/{id}/recipes                              (managed library)
 *   GET  /patients/{id}/recipes/{recipe_id}                  (managed detail)
 *   POST /patients/{id}/recipes/{recipe_id}/copy             (copy as patient)
 *   GET  /recipes/search?name=&filters=                      (Edamam search)
 *   GET  /patients/{id}/recipes/lists                        (list of lists)
 *   GET  /patients/{id}/recipes/lists/{type|id}/items        (items in list)
 *   POST /patients/{id}/recipes/lists/{type|id}/items        (save recipe to list)
 *   PUT  /patients/{id}/recipes/lists/{type|id}/items/{rid}  (edit notes)
 *   DELETE /patients/{id}/recipes/lists/{type|id}/items/{rid}
 *
 * Lists are addressed by either a numeric id OR a singleton `type` enum —
 * `patient_favourites` is the inbox the clinician's recipe generator drops
 * suggestions into, which is what shows up in the patient's app.
 *
 * Schemas verified against MNT swagger:11488-11758, 20310-20553.
 */

/** Singleton list types the server recognizes. v1's clinician saves into
 *  `patient_favourites` from the recipe generator. */
export type PatientRecipeListType =
  | 'patient_favourites'
  | 'patient_to_try'
  | 'patient_generated_suggestions'
  | 'patient_bookmarks';

/** Single recipe from `/recipes/search` (Edamam). v1's RecipeResponse —
 *  swagger:20310. The fields we actually consume are the top group; the
 *  rest are passed through verbatim so the saved-list item round-trips. */
export type RecipeResponse = {
  id: number;
  label: string;
  label_translations?: Record<string, string> | null;
  image_url?: string | null;
  url?: string | null;
  ingredients: string[];
  instructions_str?: string | null;
  food_name?: string | null;
  servings?: number | null;
  serving_unit_label?: string | null;
  serving_unit_amount?: number | null;
  /** Server reports this as a typed object; treated as opaque here so the
   *  type doesn't drift if the server adds nutrient keys. */
  nutrients?: Record<string, unknown> | null;
} & Record<string, unknown>;

export type PatientRecipeListItem = {
  id: number;
  additional_notes?: string | null;
  recipe: RecipeResponse;
};

export type PatientRecipeList = {
  id: string;
  type?: PatientRecipeListType | null;
  label: string;
  recipe_items: PatientRecipeListItem[];
};

/**
 * Legacy slim type used by the existing Recipes tab's "saved recipes" table.
 * Kept as an adapter over `RecipeResponse` until the tab is rebuilt — phase
 * 2 of the recipes work. The slim shape was a guess from the original V2
 * scaffold and doesn't match server reality.
 */
export type Recipe = {
  recipe_id: number;
  name: string;
  description?: string;
  ingredients?: string;
  instructions?: string;
  servings?: number;
  nutrition?: Record<string, number>;
};

export type RecipeInput = {
  name: string;
  description?: string;
  ingredients?: string;
  instructions?: string;
  servings?: number;
};

export type ManagedRecipeIngredient = {
  id?: number | null;
  description: string;
  food_name?: string | null;
  rxfood_id?: string | null;
  child_recipe_id?: number | null;
  serving_unit_label?: string | null;
  serving_unit_amount?: number | null;
  servings?: number | null;
};

/**
 * First-class managed recipe from `/patients/{id}/recipes`. Kept separate from
 * Edamam `RecipeResponse` (`ingredients: string[]` + open index signature).
 */
export type ManagedRecipe = {
  id: number;
  label: string;
  label_translations?: Record<string, string> | null;
  image_url?: string | null;
  url?: string | null;
  instructions_str?: string | null;
  food_name?: string | null;
  servings?: number | null;
  serving_unit_label?: string | null;
  serving_unit_amount?: number | null;
  nutrients?: Record<string, unknown> | null;
  patient_id?: number | null;
  chef_id?: number | null;
  visibility: 'private' | 'public' | 'shared';
  curation_status: 'draft' | 'published' | 'archived';
  source?: 'user' | 'chef' | 'imported' | 'edamam' | null;
  yield_amount?: number | null;
  yield_unit_label?: string | null;
  filter_tags?: string[] | null;
  ingredient_responses?: ManagedRecipeIngredient[];
};

/* -------------------------------------------------------------------------- */
/*                               ADAPTERS                                     */
/* -------------------------------------------------------------------------- */

const fromListItem = (it: PatientRecipeListItem): Recipe => ({
  recipe_id: it.recipe.id,
  name: it.recipe.label,
  description: it.additional_notes ?? undefined,
  ingredients: Array.isArray(it.recipe.ingredients) ? it.recipe.ingredients.join('\n') : undefined,
  instructions: it.recipe.instructions_str ?? undefined,
  servings: it.recipe.servings ?? undefined,
});

/* -------------------------------------------------------------------------- */
/*                                CLIENT                                      */
/* -------------------------------------------------------------------------- */

const treatStatusAsEmpty = async <T>(fn: () => Promise<T>, fallback: T, statuses: number[]): Promise<T> => {
  try {
    return await fn();
  } catch (err) {
    const status = (err as { response?: { status?: number } }).response?.status;
    if (status !== undefined && statuses.includes(status)) return fallback;
    throw err;
  }
};

export const recipesApi = {
  /* ----------------------- MANAGED RECIPE LIBRARY ----------------------- */

  async listManaged(patientId: number, query?: string): Promise<ManagedRecipe[]> {
    const res = await apiClient.get<ManagedRecipe[]>(`/patients/${patientId}/recipes`, {
      params: query?.trim() ? { q: query.trim() } : undefined,
    });
    return Array.isArray(res.data) ? res.data : [];
  },

  async getManaged(patientId: number, recipeId: number): Promise<ManagedRecipe> {
    const res = await apiClient.get<ManagedRecipe>(
      `/patients/${patientId}/recipes/${recipeId}`,
    );
    return res.data;
  },

  async copyManaged(patientId: number, recipeId: number): Promise<ManagedRecipe> {
    const res = await apiClient.post<ManagedRecipe>(
      `/patients/${patientId}/recipes/${recipeId}/copy`,
    );
    return res.data;
  },

  /* -------------------------- RECIPE SEARCH ----------------------------- */

  /**
   * Edamam-backed recipe search. v1 calls this once per generated recipe
   * name (one row of the OpenAI numbered list = one search query) so each
   * gets its own carousel of cards. `filters` is a JSON-encoded Edamam
   * filter object (`{diet:[], health:[], mealType:[]}` etc) — pass undefined
   * for no filtering.
   */
  async search(name: string, filtersJson?: string): Promise<RecipeResponse[]> {
    return treatStatusAsEmpty(
      async () => {
        const res = await apiClient.get<RecipeResponse[]>(`/recipes/search`, {
          params: { name, filters: filtersJson },
        });
        return Array.isArray(res.data) ? res.data : [];
      },
      [],
      [400, 404, 502, 503],
    );
  },

  /* -------------------------- RECIPE LISTS ------------------------------ */

  /**
   * Get the patient's recipe list of a given singleton type. Returns null
   * (not 404) when the list doesn't exist yet — the `post...item` calls
   * auto-create the singleton on first use.
   */
  async getList(
    patientId: number,
    listIdOrType: PatientRecipeListType | string,
  ): Promise<PatientRecipeList | null> {
    return treatStatusAsEmpty(
      async () => {
        const res = await apiClient.get<PatientRecipeList>(
          `/patients/${patientId}/recipes/lists/${encodeURIComponent(listIdOrType)}`,
        );
        return res.data;
      },
      null,
      [404],
    );
  },

  async listItems(
    patientId: number,
    listIdOrType: PatientRecipeListType | string,
  ): Promise<PatientRecipeListItem[]> {
    return treatStatusAsEmpty(
      async () => {
        const res = await apiClient.get<PatientRecipeListItem[]>(
          `/patients/${patientId}/recipes/lists/${encodeURIComponent(listIdOrType)}/items`,
        );
        return Array.isArray(res.data) ? res.data : [];
      },
      [],
      [404],
    );
  },

  async addItem(
    patientId: number,
    listIdOrType: PatientRecipeListType | string,
    recipeId: number,
  ): Promise<PatientRecipeListItem> {
    // v1 always resolves the singleton list to its integer id BEFORE posting
    // an item (clinician/src/components/PatientAutopilot.tsx:324). The GET
    // serves as both a lookup and a server-side auto-create — so when the
    // patient has never had a `patient_favourites` list before, the GET is
    // what brings it into existence; the subsequent POST then targets a
    // real, persisted list by its numeric id. POSTing directly with the
    // singleton string occasionally 404s in production (the lookup race
    // mentioned above), which is what surfaced as "saving doesn't work".
    let resolvedId: string | number = listIdOrType;
    if (typeof listIdOrType === 'string' && Number.isNaN(Number(listIdOrType))) {
      const list = await this.getList(patientId, listIdOrType);
      if (list?.id) resolvedId = list.id;
    }
    const res = await apiClient.post<PatientRecipeListItem>(
      `/patients/${patientId}/recipes/lists/${encodeURIComponent(String(resolvedId))}/items`,
      { recipe_id: recipeId },
    );
    return res.data;
  },

  async updateItemNotes(
    patientId: number,
    listIdOrType: PatientRecipeListType | string,
    recipeId: number,
    additionalNotes: string,
  ): Promise<PatientRecipeListItem> {
    const res = await apiClient.put<PatientRecipeListItem>(
      `/patients/${patientId}/recipes/lists/${encodeURIComponent(listIdOrType)}/items/${recipeId}`,
      { additional_notes: additionalNotes },
    );
    return res.data;
  },

  async removeItem(
    patientId: number,
    listIdOrType: PatientRecipeListType | string,
    recipeId: number,
  ): Promise<void> {
    await apiClient.delete(
      `/patients/${patientId}/recipes/lists/${encodeURIComponent(listIdOrType)}/items/${recipeId}`,
    );
  },

  /* ------------------------ LEGACY-SLIM SHIM ---------------------------- */

  /**
   * Legacy slim API used by the existing "Saved recipes" table on the
   * Recipes tab. Adapts to the real `patient_favourites` recipe-list
   * endpoints so the UI keeps working until the table is rebuilt.
   *
   * Phase 2 of the recipes work removes this shim and rebuilds the table
   * around `PatientRecipeListItem` directly so the `additional_notes`
   * field becomes editable inline.
   */
  async list(patientId: number): Promise<Recipe[]> {
    const items = await this.listItems(patientId, 'patient_favourites');
    return items.map(fromListItem);
  },

  /**
   * @deprecated Server has no "create recipe" endpoint that accepts a
   * free-form name + ingredients string — recipes come from the search
   * service. The legacy New Recipe dialog can't actually create anything;
   * this stub keeps existing callers from crashing while phase 2 either
   * removes the dialog or rebuilds it around recipe search.
   */
  async create(_patientId: number, _body: RecipeInput): Promise<Recipe> {
    throw new Error(
      'Recipe creation is not supported — use recipesApi.search() + addItem() to save a found recipe instead.',
    );
  },

  async update(_patientId: number, _recipeId: number, _body: RecipeInput): Promise<Recipe> {
    throw new Error('Recipe.update is not supported — use updateItemNotes() instead.');
  },

  async deleteRecipe(patientId: number, recipeId: number): Promise<void> {
    await this.removeItem(patientId, 'patient_favourites', recipeId);
  },
};
